/**
 * THE LINE EDITOR — product first, quantity in its own unit (F04, 21 Sep).
 *
 * One editor for the quotation form and the order form: a row is a PRODUCT
 * from the catalogue (or, for a gas the catalogue does not carry, a gas by
 * name), a quantity, and the unit the customer thinks in — cylinders, Nm³ or
 * kg. The preview says how many cylinders a volume takes, through the
 * product's stated content and nothing else.
 *
 * A GAS BY NAME MAY BE ASKED FOR IN GAS. It used to be whole cylinders only —
 * 0066's rule that every line had to come to a cylinder count — and the editor
 * showed "whole cylinders only" where the unit chooser belongs. The owner's 9
 * October ruling removed that: a customer orders 40 Nm3 of oxygen, and which
 * shells carry it is settled in the yard when the load is picked, each going
 * out full (0165/0166). So the chooser is offered on every row, and where
 * nothing states a content the preview says the cylinder count is not yet
 * known rather than refusing the unit.
 *
 * Pure on purpose: the screens fetch the products and hand them in, so this
 * renders in a unit test with no server.
 */
import * as React from 'react';
import { Button } from './ui.js';
import { words } from './format.js';

export type LineUnit = 'cylinder' | 'm3' | 'kg';

export interface SkuOption {
  skuId: number; code: string; gasCode: string; gradeCode: string;
  /** 11 Oct — what a person recognises a product by (CTS1's "Industrial Oxygen Gas · IS 309"): the names, the purity, the unit it sells in, the gas's standard. */
  gasName?: string; gradeName?: string; purityText?: string | null; gasUnit?: string; specification?: string | null;
  /** 0161 — a product is a gas at a grade; the container is optional, so these may be absent. */
  sizeCode: string | null; waterCapacityL: number | null; nominalQty: number | null; nominalUnit: string | null;
}

export interface LineRow {
  /** '' = a gas by name */
  skuId: string;
  gasService: string;
  waterCapacityL: string;
  qty: string;
  unit: LineUnit;
  /** 0162 — the rate this offer states, in rupees per `unit`. '' = price it from the customer's list. */
  rupees: string;
  note: string;
}

export function emptyLine(): LineRow {
  // 0163 — the owner, 9 October: a quotation is always in Nm3 or kg. A line
  // starts in gas; an ORDER may still be placed in cylinders and says so.
  return { skuId: '', gasService: '', waterCapacityL: '', qty: '1', unit: 'm3', rupees: '', note: '' };
}

export const UNIT_WORDS: Record<LineUnit, string> = { cylinder: 'Cylinders', m3: 'Nm³', kg: 'kg' };

const num = (s: string): number | null => { const v = Number(s); return s.trim() && Number.isFinite(v) ? v : null; };

/** "≈ 16 cylinders" for a volume, "3 cylinders · 19.8 Nm³" for cylinders, or why it cannot say. */
export function previewOf(row: LineRow, skus: readonly SkuOption[]): string | null {
  const qty = num(row.qty);
  if (qty === null || qty <= 0) return null;
  const sku = skus.find((s) => String(s.skuId) === row.skuId);
  if (!sku) {
    if (row.unit === 'cylinder') return Number.isInteger(qty) ? null : 'whole cylinders only';
    // 0165 — a quantity of gas with nothing to convert through: honest, and
    // settled in the yard rather than guessed here.
    return 'cylinders settled when the load is picked';
  }
  const hasBasis = sku.nominalQty !== null && (sku.nominalUnit === 'm3' || sku.nominalUnit === 'kg');
  if (row.unit === 'cylinder') {
    if (!Number.isInteger(qty)) return 'whole cylinders only';
    if (!hasBasis) return null;
    const content = Math.round(qty * sku.nominalQty! * 1000) / 1000;
    return `${qty} cylinder${qty === 1 ? '' : 's'} · ${content} ${UNIT_WORDS[sku.nominalUnit as LineUnit]}`;
  }
  if (!hasBasis) return sku.sizeCode
    ? `no content stated for ${sku.sizeCode} — ask in cylinders, or state the size's content in the catalogue`
    : 'cylinders settled when the load is picked';
  if (sku.nominalUnit !== row.unit) return `${sku.sizeCode}'s content is stated in ${UNIT_WORDS[sku.nominalUnit as LineUnit]}, not ${UNIT_WORDS[row.unit]}`;
  const vessels = Math.max(1, Math.ceil(qty / sku.nominalQty! - 1e-9));
  return `≈ ${vessels} cylinder${vessels === 1 ? '' : 's'}`;
}

export type LineBody = { qty: number; unit?: LineUnit; note?: string; unitPricePaise?: number }
  & ({ skuId: number } | { gasService: string; waterCapacityL?: number });

/** Rupees as typed, into paise. undefined = unpriced; null = not money. */
export function rupeesToPaiseOrNull(v: string): number | null | undefined {
  const t = v.trim();
  if (!t) return undefined;
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(Number(t) * 100);
}

/** The bodies the door takes: a product OR a gas, the unit only when it is not cylinders, a note only when there is one. */
export function toLineBodies(rows: readonly LineRow[]): LineBody[] {
  const out: LineBody[] = [];
  for (const r of rows) {
    const qty = num(r.qty);
    if (qty === null || qty <= 0) continue;
    const paise = rupeesToPaiseOrNull(r.rupees);
    const extras = {
      ...(r.unit !== 'cylinder' ? { unit: r.unit } : {}),
      ...(typeof paise === 'number' ? { unitPricePaise: paise } : {}),
      ...(r.note.trim() ? { note: r.note.trim() } : {}),
    };
    if (r.skuId) { out.push({ skuId: Number(r.skuId), qty, ...extras }); continue; }
    if (!r.gasService.trim()) continue;
    const size = num(r.waterCapacityL);
    out.push({ gasService: r.gasService.trim(), ...(size !== null ? { waterCapacityL: size } : {}), qty, ...extras });
  }
  return out;
}

/** The row a form starts with: the first product in the list, in the unit it sells in; a gas by name only when the catalogue is empty. */
export function firstLine(skus: readonly SkuOption[], quotedInGas: boolean): LineRow {
  const p = skus[0];
  const base = emptyLine();
  if (!p) return quotedInGas ? base : { ...base, unit: 'cylinder' };
  const u = p.gasUnit === 'm3' || p.gasUnit === 'kg' ? p.gasUnit : (quotedInGas ? 'm3' : 'cylinder');
  return { ...base, skuId: String(p.skuId), unit: u };
}

/** "Industrial Oxygen · 99.5% · B46 46.7 L · per Nm³  [OXY-COMM-B46]" — the code last, small, for the people who know it. */
export function productLabel(p: SkuOption): string {
  const parts = [`${p.gasName || p.gasCode} ${p.gradeName || p.gradeCode}`.trim()];
  if (p.purityText && !parts[0]!.includes(p.purityText)) parts.push(p.purityText);
  if (p.sizeCode) parts.push(`${p.sizeCode}${p.waterCapacityL !== null ? ` ${p.waterCapacityL} L` : ''}`);
  if (p.gasUnit) parts.push(`per ${UNIT_WORDS[p.gasUnit as LineUnit] ?? p.gasUnit}`);
  return `${parts.join(' · ')}  [${p.code}]`;
}

const inr = (paise: number) => `₹${(paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Σ qty × rate over the rows that carry a typed rate, in paise; null when no row is priced yet. */
export function linesSubtotalPaise(rows: readonly LineRow[]): number | null {
  let sum = 0; let any = false;
  for (const r of rows) {
    const qty = num(r.qty); const paise = rupeesToPaiseOrNull(r.rupees);
    if (qty === null || typeof paise !== 'number') continue;
    sum += Math.round(qty * paise); any = true;
  }
  return any ? sum : null;
}

export function LineEditor({ rows, skus, gases = [], onChange, idPrefix, quotedInGas = false }: {
  rows: readonly LineRow[]; skus: readonly SkuOption[]; onChange: (rows: LineRow[]) => void; idPrefix: string;
  /**
   * 0175 — the gas names this company uses, to choose from. A typed gas is how
   * one customer ends up with three rates for one gas: the rate card is keyed
   * by this very string.
   */
  gases?: readonly { name: string; cylinders: number }[];
  /**
   * 0163 — the owner, 9 October: "quotation will be always in either nm3 or
   * kg not in cylinders". An ORDER may still be placed in cylinders, and this
   * same editor writes both, so the document says which it is rather than the
   * editor guessing.
   */
  quotedInGas?: boolean;
}) {
  const set = (i: number, patch: Partial<LineRow>) => onChange(rows.map((r, n) => (n === i ? { ...r, ...patch } : r)));
  // the order form's ids have no prefix (gas-0, qty-0 — the browser suite types into them); the quote form's carry one
  const id = (name: string, i: number) => `${idPrefix ? `${idPrefix}-` : ''}${name}-${i}`;
  // 11 Oct — CTS1 lists products by NAME, grouped by gas; the catalogue code
  // rides at the end for whoever knows it. A product with no container reads
  // "Oxygen High Purity · 99.99% · per Nm³", never "null (null L)".
  const groups = new Map<string, SkuOption[]>();
  for (const p of skus) { const k = p.gasName || p.gasCode; const g = groups.get(k); if (g) g.push(p); else groups.set(k, [p]); }
  const unitOf = (p: SkuOption | undefined): LineUnit | null => (p?.gasUnit === 'm3' || p?.gasUnit === 'kg' ? p.gasUnit : null);
  const subtotal = linesSubtotalPaise(rows);
  return (
    <>
      {rows.map((r, i) => {
        const preview = previewOf(r, skus);
        // A line names a PRODUCT or a GAS. Naming a gas keeps the optional
        // size box either way; only a gas the list does not carry needs the
        // name typed. (Tying the size box to "typed" made it vanish the moment
        // the typed name matched a listed gas, mid-entry.)
        const namesGas = !r.skuId;
        const typed = namesGas && !gases.some((g) => g.name === r.gasService);
        const sku = skus.find((p) => String(p.skuId) === r.skuId);
        const locked = unitOf(sku);
        const units: readonly LineUnit[] = locked ? [locked] : (quotedInGas ? ['m3', 'kg'] : ['cylinder', 'm3', 'kg']);
        const qty = num(r.qty); const paise = rupeesToPaiseOrNull(r.rupees);
        const amount = qty !== null && typeof paise === 'number' ? Math.round(qty * paise) : null;
        return (
          <div className={`line-row${namesGas ? ' by-name' : ''}`} key={i} data-line={i}>
            <div className="field"><label htmlFor={id('product', i)}>Product</label>
              <select id={id('product', i)} value={r.skuId || (r.gasService && gases.some((g) => g.name === r.gasService) ? `gas:${r.gasService}` : '')}
                      onChange={(e: { target: { value: string } }) => {
                        const v = e.target.value;
                        if (v.startsWith('gas:')) { set(i, { skuId: '', gasService: v.slice(4) }); return; }
                        const p = skus.find((x) => String(x.skuId) === v);
                        const u = unitOf(p);
                        set(i, { skuId: v, ...(v ? {} : { gasService: '' }), ...(u ? { unit: u } : v ? {} : { unit: (quotedInGas ? 'm3' : 'cylinder') as LineUnit }) });
                      }}>
                {Array.from(groups.entries()).map(([gas, ps]) => (
                  <optgroup key={gas} label={gas}>
                    {ps.map((p) => <option key={p.skuId} value={String(p.skuId)}>{productLabel(p)}</option>)}
                  </optgroup>
                ))}
                {gases.length ? (
                  <optgroup label="A gas, no particular product">
                    {gases.map((g) => <option key={g.name} value={`gas:${g.name}`}>{words(g.name)}{g.cylinders ? ` · ${g.cylinders} cylinder${g.cylinders === 1 ? '' : 's'}` : ''}</option>)}
                  </optgroup>
                ) : null}
                <option value="">Something else — type the gas</option>
              </select></div>
            {typed ? (
              <div className="field"><label htmlFor={id('gas', i)}>Gas</label>
                <input id={id('gas', i)} type="text" value={r.gasService} placeholder="e.g. oxygen" onChange={(e: { target: { value: string } }) => set(i, { gasService: e.target.value })} /></div>
            ) : null}
            {namesGas ? (
              <div className="field"><label htmlFor={id('size', i)}>Size (L)</label>
                <input id={id('size', i)} type="number" value={r.waterCapacityL} placeholder="any" onChange={(e: { target: { value: string } }) => set(i, { waterCapacityL: e.target.value })} /></div>
            ) : null}
            <div className="field"><label htmlFor={id('qty', i)}>Qty</label>
              <input id={id('qty', i)} type="number" step={r.unit === 'cylinder' ? 1 : 'any'} value={r.qty}
                     onChange={(e: { target: { value: string } }) => set(i, { qty: e.target.value })} /></div>
            {/* 0165/0166 — the unit chooser is on EVERY row; with a product it
                is locked to the unit the product sells in, as CTS1 does, so the
                rate cannot drift from what billing uses. */}
            <div className="field"><label htmlFor={id('unit', i)}>Unit</label>
              <select id={id('unit', i)} value={r.unit} onChange={(e: { target: { value: string } }) => set(i, { unit: e.target.value as LineUnit })}
                      title={locked ? `${sku?.gasName ?? 'this product'} sells per ${UNIT_WORDS[locked]}` : undefined}>
                {units.map((u) => <option key={u} value={u}>{UNIT_WORDS[u]}</option>)}
              </select></div>
            {/* 0162 — the owner, 6 Oct: "where do i add the price to sell this
                gas". Blank still prices from the customer's list at issue. */}
            <div className="field"><label htmlFor={id('rate', i)}>{`Rate ₹ / ${UNIT_WORDS[r.unit]}`}</label>
              <input id={id('rate', i)} type="text" value={r.rupees} placeholder="from the price list"
                     onChange={(e: { target: { value: string } }) => set(i, { rupees: e.target.value })} /></div>
            <div className="field"><label htmlFor={id('note', i)}>Note</label>
              <input id={id('note', i)} type="text" value={r.note} placeholder="optional" onChange={(e: { target: { value: string } }) => set(i, { note: e.target.value })} /></div>
            <div className="field"><label>&nbsp;</label>
              {rows.length > 1 ? <Button small kind="danger" onClick={() => onChange(rows.filter((_, n) => n !== i))}>×</Button> : <span />}</div>
            <div className="line-note">
              <span data-spec={i}>{sku?.specification ? `Specification: ${sku.specification}` : typed ? 'A gas the catalogue does not carry — it is priced from the customer\u2019s card or the rate typed here.' : ''}</span>
              <span>{preview ? <span data-preview={i}>{preview}</span> : null}{amount !== null ? <> · Amount <b data-amount={i}>{inr(amount)}</b></> : null}</span>
            </div>
          </div>
        );
      })}
      <div className="line-sum">
        <Button small onClick={() => onChange([...rows, emptyLine()])}>+ Add another line</Button>
        {subtotal !== null ? <span>Sub-total <b data-subtotal>{inr(subtotal)}</b> <span className="muted">(Σ qty × rate · GST as the terms say)</span></span> : <span className="muted">{quotedInGas ? 'Rates left blank are read from the customer’s card when the offer is issued.' : 'Priced from the customer’s card; a rate typed here overrides it for this order.'}</span>}
      </div>
    </>
  );
}
